import React, { Dispatch } from 'react';
import { Value as DateValue } from 'react-date-picker/dist/cjs/shared/types';
import { DatePicker } from '~/components/Form/DatePicker';
import { Select } from '~/components/Select';
import { GapOptionType, SearchFilterType, SearchValuesType } from '~/services';
import { dateAtLocalTimezone } from '~/util';
import { GapOption, PCPOption, PCPOptionType } from '.';
import { FormValuesEvent } from '../Search';

type LoadOptionType = (inputValue: string) => void;
type ValueType = null | string | SearchFilterType[keyof SearchFilterType] | DateValue;
export type SearchInputChangeEvent = {
    target: { value: ValueType; id: string; formVals?: SearchValuesType };
};
type ChangeEventHandler = (x: SearchInputChangeEvent) => void;
type SearchInputProps = {
    label: string;
    value: ValueType;
    handleChange: Dispatch<FormValuesEvent> | ChangeEventHandler;
    options?: ValueType | ValueType[] | LoadOptionType;
    errorMsg?: string;
};
export function SearchInput({
    label,
    value,
    handleChange,
    options = [],
    errorMsg,
}: SearchInputProps) {
    let title =
        label.match(/[a-z]/g) === null
            ? label
            : label.charAt(0).toUpperCase() + label.slice(1).replace(/[A-Z]./g, ' $&');
    if (label === 'disenrolled') title = 'PCP Assignment';
    if (label === 'riskFactor') title = 'Social Risk Factor';

    const isFullRow = [
        'clinic',
        'PCP',
        'planType',
        'riskBand',
        'openGaps',
        'GMPI',
        'insurance',
        'disenrolled',
        'riskFactor',
    ].includes(label);

    const inputComponent = React.useMemo(() => {
        switch (label) {
            case 'DOB':
                return (
                    <DatePicker
                        name={label}
                        value={dateAtLocalTimezone(value)}
                        minDate={new Date('1821-04-19')}
                        onChange={(val) =>
                            (handleChange as ChangeEventHandler)({
                                target: {
                                    value: val,
                                    id: label,
                                },
                            })
                        }
                    />
                );
            case 'PCP':
                return (
                    <Select
                        components={{ Option: PCPOption }}
                        isMulti
                        menuPosition="fixed"
                        options={options as PCPOptionType[]}
                        label={label}
                        placeholder="Any PCP"
                        value={value as PCPOptionType[]}
                        onChange={(val) =>
                            (handleChange as ChangeEventHandler)({
                                target: {
                                    value: val as ValueType,
                                    id: label,
                                },
                            })
                        }
                    />
                );
            case 'clinic':
            case 'riskBand':
            case 'insurance':
                return (
                    <Select
                        isMulti
                        menuPosition="fixed"
                        options={options as ValueType[]}
                        label={label}
                        placeholder={
                            label === 'riskBand'
                                ? 'All'
                                : `Any ${label.charAt(0).toUpperCase()}${label.slice(1)}`
                        }
                        value={value}
                        onChange={(val) =>
                            (handleChange as ChangeEventHandler)({
                                target: {
                                    value: val as ValueType,
                                    id: label,
                                },
                            })
                        }
                    />
                );
            case 'planType':
            case 'disenrolled':
            case 'riskFactor':
                return (
                    <Select
                        menuPosition="fixed"
                        options={options as ValueType[]}
                        label={label}
                        value={value}
                        onChange={(val) =>
                            (handleChange as ChangeEventHandler)({
                                target: {
                                    value: val,
                                    id: label,
                                },
                            })
                        }
                    />
                );
            case 'openGaps':
                return (
                    <Select
                        menuPosition="fixed"
                        menuPlacement="top"
                        async
                        isMulti
                        defaultOptions
                        loadOptions={options as LoadOptionType}
                        label={label}
                        value={value as GapOptionType[]}
                        classNamePrefix="option-with-description"
                        components={{ Option: GapOption }}
                        placeholder="Any Gaps"
                        onChange={(val: GapOptionType[]) =>
                            (handleChange as ChangeEventHandler)({
                                target: {
                                    value: val,
                                    id: label,
                                },
                            })
                        }
                    />
                );
            default:
                return (
                    <input
                        type="text"
                        className="form-control"
                        id={label}
                        value={value as string}
                        onChange={(e) =>
                            handleChange({ target: { value: e.target.value, id: e.target.id } })
                        }
                    />
                );
        }
    }, [handleChange, label, options, value]);

    return (
        <>
            <label
                htmlFor={label}
                className={`search-input-label col-3 col-form-label ${
                    !isFullRow ? 'col-lg-2' : ''
                }`}
            >
                {title}
            </label>
            <div className={`search-input col-9 mb-2 ${!isFullRow ? 'col-lg-4' : ''}`}>
                {inputComponent}
                {errorMsg && <span className="d-block zp-form-validation-message">{errorMsg}</span>}
            </div>
        </>
    );
}
